import type { UiMessageModule } from "./merge";

export const dashboardOrgPageMessages = {
	en: {
		announcements: {
			blog: "Blog",
			changelogNotifications: "Changelog notifications",
			seeFullChangelog: "See full changelog ->",
			unreadAnnouncement: "{count} unread announcement",
			unreadAnnouncements: "{count} unread announcements",
			update: "Update",
			viewAll: "View all",
			whatsNew: "What's New",
		},
		dashboardChart: {
			apiKeyBreakdown: "API Key Breakdown:",
			apiKeyLabel: "API key:",
			apiKeyUsageOverview: "API Key Usage Overview",
			cachedInput: "Cached Input",
			cost: "Cost",
			errorLoading: "Error loading activity data",
			estimatedCost: "estimated cost",
			filteredByProject: "Project: {project}",
			input: "Input",
			last24Hours: "last 24 hours",
			lastDays: "last {count} days",
			lastHours: "last {count} hours",
			loading: "Loading activity data...",
			modelBreakdown: "Model Breakdown:",
			modelSeries: "model",
			modelLabel: "Model:",
			modelUsageOverview: "Model Usage Overview",
			noActivity: "No activity data available",
			noProject: "No project selected",
			noProjectDescription: "Please select a project to view activity data",
			output: "Output",
			periodDays: "{count} days",
			requests: "Requests",
			selectMetric: "Select metric",
			showLess: "Show less",
			showMore: "+{count} more",
			stackedDescription: "Stacked {series} {metric} over {period}",
			tokens: "tokens",
			apiKeySeries: "API key",
		},
		dashboardDialogs: {
			cancel: "Cancel",
			createNewOrganization: "Create New Organization",
			createNewOrganizationDescription:
				"Create a new organization to group your projects.",
			createNewProject: "Create New Project",
			createNewProjectDescription: "Create a new project in {organization}.",
			createOrganization: "Create Organization",
			createProject: "Create Project",
			creating: "Creating...",
			newOrganization: "New Organization",
			newProject: "New Project",
			noProjectsYet: "No projects yet",
			organizationCreated: "Organization created successfully",
			organizationCreatedDescription: "{name} has been created.",
			organizations: "Organizations",
			organizationFallback: "the selected organization",
			projectsInOrganization: "Projects in {organization}",
			projectCreated: "Project created successfully",
			projectCreatedDescription: "{name} has been created.",
			selectOrganization: "Select Organization",
			selectProject: "Select Project",
			name: "Name",
		},
		dashboardHome: {
			addCreditsFirst:
				"Add credits to your organization to unlock all paid models. Free models are always available via the playground.",
			availableBalance: "Available balance",
			cacheHitSuffix: "{rate}% cached",
			cachedTokensAndCost: "Cached Tokens & Cost",
			cachedTokensTooltip:
				"Cached input tokens are already included in the Input Tokens & Cost total above. This card breaks them out so you can see how much of the input was served from cache.",
			commonTasks: "Common tasks you might want to perform",
			costBreakdown: "Cost Breakdown",
			costBreakdownDescription:
				"Estimated costs by provider and storage for this project",
			costs: "Costs",
			createApiKey: "Create API Key",
			creditsNeverExpire: "Credits never expire. Only pay for what you use.",
			dashboard: "Dashboard",
			discountsFrom: "Discounts from {from} - {to}",
			docs: "Docs",
			freeModels: "Free models",
			getStarted: "Get Started",
			inputTokensAndCost: "Input Tokens & Cost",
			loading: "Loading...",
			manageApiKeys: "Manage API Keys",
			modelUsage: "Model Usage",
			mostUsedModel: "Most Used Model",
			organization: "Organization: {organization}",
			organizationCredits: "Organization Credits",
			payAsYouGo: "Pay as you go",
			planAllowsMax: "{plan} plan allows maximum {max} API keys per project",
			playground: "Playground",
			project: "Project: {project}",
			promptTokensCost: "Prompt tokens and associated cost",
			provider: "Provider: {provider}",
			providerKeys: "Provider Keys",
			providerPricingReference: "Provider pricing for reference",
			quickActions: "Quick Actions",
			requestCostSuffix: "${amount} requests",
			requests: "Requests",
			secureCheckout: "Secure checkout",
			setupCalls:
				"You made {count} calls during setup! Now integrate doteb in your own code.",
			setupFirstCall:
				"You made your first call during setup! Now integrate doteb in your own code.",
			storageCostSuffix: "${amount} storage",
			topUpToStart: "Top up to start making requests",
			totalCost: "Total Cost",
			totalRequests: "Total Requests",
			totalSavings: "Total Savings",
			tryFreeModels: "Try free models",
			tryFreeModelsDescription: "Try {link} without topping up.",
			tryFreeModelsPrefix: "Try ",
			tryFreeModelsSuffix: " without topping up.",
			usageMetrics: "Usage & Metrics",
			usageOverview: "Usage Overview",
			viewActivity: "View Activity",
			withDateRange: "{from} - {to}",
			codeOneLine:
				"Integrate doteb in 1 line - just change your base URL.",
			outputTokensAndCost: "Output Tokens & Cost",
			completionTokensCost: "Completion tokens and associated cost",
			cacheTokensCost: "Tokens and cost served from cache (if supported)",
			secureCheckoutDescription: "Powered by Stripe. Cancel or refund anytime.",
		},
		dashboardOverviewCards: {
			cacheHealth: "Error rate and cache health",
			cacheHitRate: "Cache hit rate",
			cachedResponses: "{count} cached responses",
			costBreakdown: "Cost Breakdown",
			costBreakdownDescription:
				"Estimated costs by provider and storage for this project",
			dailyUsageOverview: "Daily usage overview (last few days)",
			errorRate: "Error rate",
			errorsReliability: "Errors & Reliability",
			failedOfRequests: "{failed} failed of {total} requests",
			highestErrorDays: "Days with highest error rate",
			inferenceReference: "inference (ref.)",
			latestUsage: "Latest usage for this project",
			loadingRecentActivity: "Loading recent activity...",
			loadingReliability: "Loading reliability data...",
			noActivity: "No activity data available",
			overallErrorCache: "Overall error and cache rates for this period",
			recentActivity: "Recent Activity",
			requestsTokens: "{requests} requests - {tokens} tokens",
			saved: "-${amount} saved",
		},
		devPassCard: {
			cancelAnytime: "/mo - cancel anytime",
			collapse: "Collapse DevPass card",
			expand: "Expand DevPass card",
			open: "Open DevPass",
			separateProduct: "Separate product",
			valueDescription:
				"Fixed-price monthly plans for Claude Code, OpenCode, Cursor & every coding tool.",
			valueHighlight: "$1 turns into $3",
			valueSuffix: "of model usage at provider rates.",
		},
		orgDiscounts: {
			activeDescription:
				"Active discounts applied to your organization's API usage",
			all: "All",
			discount: "Discount",
			discountOff: "{percent} off",
			expires: "Expires",
			expiresLabel: "Expires:",
			global: "Global",
			model: "Model",
			modelLabel: "Model:",
			never: "Never",
			noActive: "No active discounts",
			noActiveDescription:
				"When discounts are applied to your organization, they'll appear here.",
			noOrganization: "No organization selected",
			org: "Org",
			provider: "Provider",
			providerLabel: "Provider:",
			reason: "Reason",
			scope: "Scope",
			title: "Your Discounts",
		},
		orgPreferences: {
			billingEmail: "Billing Email",
			billingEmailDescription:
				"Manage your organization's billing email address.",
			organizationId: "Organization ID",
			organizationIdDescription:
				"Use this ID when referencing your organization in the API or with support.",
			organizationName: "Organization Name",
			organizationNameDescription: "Manage your organization's name.",
			title: "Preferences",
		},
		orgReferrals: {
			avgMonthlySpend: "Avg monthly spend per user",
			bonusQuestion: "Want to give referred users a top-up bonus?",
			bonusText:
				"Contact us at {email} to enable a bonus on your invitation page, so referred users get up to 50% bonus credits on their first top-up.",
			copyDirectLink: "Copy direct referral link",
			copyInvitationLink: "Copy invitation page link",
			directLink: "Direct link",
			directLinkDescription: "Sends people to the homepage",
			earnCredits: "Earn Credits",
			earnCreditsDescription: "Get 1% of their LLM spending as credits",
			earningsNote: "Earnings are calculated after any discounts are applied",
			eligibleDescription:
				"You need to top up at least $100 in credits to become eligible for the referral program",
			example: "Example",
			estimateDisclaimer:
				"This is a simplified estimate. Actual credits depend on real usage and discounts.",
			estimateFormula:
				"{users} users x ${spend} monthly spend x 1% = ${credits} in credits every month.",
			estimatedMonthlyCredits: "Estimated monthly credits",
			howItWorks: "How It Works",
			howItWorksDescription: "Earn credits by referring new users",
			invitationDescription: "Personalized landing page with your org's name",
			invitationPage: "Invitation page",
			lifetimeStats: "Lifetime stats from your referral program",
			noOrganization: "No organization selected",
			notEligible: "Not Eligible Yet",
			notes: "Important Notes",
			payoutNote:
				"Referral credits can be used for LLM usage but cannot be paid out",
			referralLinkDescription:
				"Choose how you want to share your referral. Both options track your referral the same way.",
			referralPerformance: "Referral performance and earnings",
			shareLink: "Share Your Link",
			shareLinkDescription: "Send your referral link to friends and colleagues",
			signUp: "They Sign Up",
			signUpDescription: "When they create an account using your link",
			title: "Referrals",
			totalEarnings: "Total Earnings",
			totalTopUps: "Your total top-ups",
			unlockAmount: "${amount} more to unlock referrals",
			unlimitedReferrals: "There is no limit to how many users you can refer",
			usersReferred: "Users Referred",
			creditsAddedAutomatically:
				"Credits are added to your account automatically",
			onceEligible:
				"Once eligible, you'll earn 1% of all LLM spending from users you refer. The credits are added directly to your account balance.",
			yourStats: "Your Stats",
		},
		referralBanner: {
			email: "Email",
			howFound: "How did you find us?",
			other: "Other",
			recommendation: "Recommendation",
			search: "Search",
			skip: "Skip",
			sourceDescription:
				"This helps us understand how people discover doteb.",
		},
		referralDialog: {
			avgMonthlySpend: "Avg monthly spend per user",
			creditsPercent: "1% of referred users' LLM spending",
			earnCreditsDescription: "Earn {percent} from every user you refer.",
			earnCreditsPercent: "1% of all LLM spending",
			estimateDisclaimer:
				"This is a simplified estimate. Actual credits depend on real usage and discounts.",
			estimateFormula:
				"{users} users x ${spend} monthly spend x 1% = ${credits} in credits every month.",
			estimatedMonthlyCredits: "Estimated monthly credits",
			example: "Example",
			howItWorks: "How it works",
			inviteEarn: "Invite & earn credits",
			manageReferralSettings: "Manage referral settings",
			monthlyGoal: "Goal: ${amount}",
			referredUsers: "Referred Users",
			runNumbers: "Run the numbers",
			seeCreditsStack: "See your credits stack",
			seeCreditsStackDescription:
				"Estimate how much you could earn each month with your referral link.",
			shareLink: "Share your link",
			stepCopy:
				"Copy your personalized referral link from the dashboard and share it with users and teams who could benefit from doteb.",
			stepEarn:
				"Automatically earn {percent} spending as credits. Track earnings in real-time from your dashboard.",
			stepEarnPercent: "1% of their LLM",
			stepTopUp:
				"Top up at least {amount} in credits on your organization to become eligible and access your unique referral link.",
			thisMonth: "This month",
			trackEarnings: "Track your monthly earnings",
		},
		transactions: {
			amount: "Amount",
			date: "Date",
			description: "Description",
			errorDescription: "Error loading transaction history",
			failedLoad: "Failed to load transactions. Please try again later.",
			history: "Transaction History",
			loadingDescription: "Loading your organization's transaction history...",
			noOrganization: "Please select an organization to view transactions.",
			noTransactions: "No transactions found",
			organization: "Organization: {organization}",
			status: "Status",
			statuses: {
				completed: "completed",
				failed: "failed",
				pending: "pending",
			},
			title: "Transactions",
			types: {
				credit_refund: "Credit Refund",
				credit_topup: "Credit Top-up",
				subscription_cancellation: "Subscription Cancellation",
				subscription_renewal: "Subscription Renewal",
				subscription_start: "Subscription Start",
			},
			type: "Type",
			viewHistory:
				"View your organization's transaction history and billing details",
		},
		unauthorized: {
			goToDashboard: "Go to your dashboard",
			message: "You don't have access to this {resource}, or it doesn't exist.",
			organization: "organization",
			project: "project",
			title: "Unauthorized",
		},
	},
	"zh-CN": {
		announcements: {
			blog: "博客",
			changelogNotifications: "更新通知",
			seeFullChangelog: "查看完整更新日志 ->",
			unreadAnnouncement: "{count} 条未读公告",
			unreadAnnouncements: "{count} 条未读公告",
			update: "更新",
			viewAll: "查看全部",
			whatsNew: "最新动态",
		},
		dashboardChart: {
			apiKeyBreakdown: "API 密钥明细：",
			apiKeyLabel: "API 密钥：",
			apiKeyUsageOverview: "API 密钥用量概览",
			cachedInput: "缓存输入",
			cost: "费用",
			errorLoading: "加载活动数据失败",
			estimatedCost: "预估费用",
			filteredByProject: "项目：{project}",
			input: "输入",
			last24Hours: "最近 24 小时",
			lastDays: "最近 {count} 天",
			lastHours: "最近 {count} 小时",
			loading: "正在加载活动数据...",
			modelBreakdown: "模型明细：",
			modelSeries: "模型",
			modelLabel: "模型：",
			modelUsageOverview: "模型用量概览",
			noActivity: "暂无活动数据",
			noProject: "未选择项目",
			noProjectDescription: "请选择项目以查看活动数据",
			output: "输出",
			periodDays: "{count} 天",
			requests: "请求",
			selectMetric: "选择指标",
			showLess: "收起",
			showMore: "+{count} 更多",
			stackedDescription: "按 {series} 堆叠展示 {period} 的 {metric}",
			tokens: "token",
			apiKeySeries: "API 密钥",
		},
		dashboardDialogs: {
			cancel: "取消",
			createNewOrganization: "新建组织",
			createNewOrganizationDescription: "创建新组织来管理你的项目。",
			createNewProject: "新建项目",
			createNewProjectDescription: "在 {organization} 中创建新项目。",
			createOrganization: "创建组织",
			createProject: "创建项目",
			creating: "创建中...",
			newOrganization: "新建组织",
			newProject: "新建项目",
			noProjectsYet: "还没有项目",
			organizationCreated: "组织创建成功",
			organizationCreatedDescription: "{name} 已创建。",
			organizations: "组织",
			organizationFallback: "当前选中的组织",
			projectsInOrganization: "{organization} 中的项目",
			projectCreated: "项目创建成功",
			projectCreatedDescription: "{name} 已创建。",
			selectOrganization: "选择组织",
			selectProject: "选择项目",
			name: "名称",
		},
		dashboardHome: {
			addCreditsFirst:
				"向组织充值额度即可解锁所有付费模型。免费模型始终可在 Playground 中使用。",
			availableBalance: "可用余额",
			cacheHitSuffix: "{rate}% 命中缓存",
			cachedTokensAndCost: "缓存 Token 与费用",
			cachedTokensTooltip:
				"缓存输入 token 已包含在上方的输入 Token 与费用总计中。此卡片单独展示其中有多少输入来自缓存。",
			commonTasks: "你可能常用的操作",
			costBreakdown: "费用明细",
			costBreakdownDescription: "按供应商和存储统计此项目的预估费用",
			costs: "费用",
			createApiKey: "创建 API 密钥",
			creditsNeverExpire: "额度不会过期。只按实际使用付费。",
			dashboard: "控制台",
			discountsFrom: "{from} - {to} 的折扣",
			docs: "文档",
			freeModels: "免费模型",
			getStarted: "开始使用",
			inputTokensAndCost: "输入 Token 与费用",
			loading: "加载中...",
			manageApiKeys: "管理 API 密钥",
			modelUsage: "模型用量",
			mostUsedModel: "最常用模型",
			organization: "组织：{organization}",
			organizationCredits: "组织额度",
			payAsYouGo: "按量付费",
			planAllowsMax: "{plan} 套餐每个项目最多允许 {max} 个 API 密钥",
			playground: "Playground",
			project: "项目：{project}",
			promptTokensCost: "提示词 token 及相关费用",
			provider: "供应商：{provider}",
			providerKeys: "供应商密钥",
			providerPricingReference: "按供应商价格估算，仅供参考",
			quickActions: "快捷操作",
			requestCostSuffix: "${amount} 请求费用",
			requests: "请求",
			secureCheckout: "安全支付",
			setupCalls:
				"你在配置过程中已完成 {count} 次调用！现在可以在自己的代码中集成 doteb。",
			setupFirstCall:
				"你在配置过程中已完成首次调用！现在可以在自己的代码中集成 doteb。",
			storageCostSuffix: "${amount} 存储费用",
			topUpToStart: "充值后开始发起请求",
			totalCost: "总费用",
			totalRequests: "总请求数",
			totalSavings: "总节省",
			tryFreeModels: "试用免费模型",
			tryFreeModelsDescription: "无需充值即可试用{link}。",
			tryFreeModelsPrefix: "无需充值即可试用",
			tryFreeModelsSuffix: "。",
			usageMetrics: "用量与指标",
			usageOverview: "用量概览",
			viewActivity: "查看活动",
			withDateRange: "{from} - {to}",
			codeOneLine: "只需修改 base URL，即可用一行配置集成 doteb。",
			outputTokensAndCost: "输出 Token 与费用",
			completionTokensCost: "补全 token 及相关费用",
			cacheTokensCost: "由缓存提供的 token 和费用（如模型支持）",
			secureCheckoutDescription: "由 Stripe 提供支付能力。可随时取消或退款。",
		},
		dashboardOverviewCards: {
			cacheHealth: "错误率与缓存健康度",
			cacheHitRate: "缓存命中率",
			cachedResponses: "{count} 个缓存响应",
			costBreakdown: "费用明细",
			costBreakdownDescription: "按供应商和存储统计此项目的预估费用",
			dailyUsageOverview: "每日用量概览（最近几天）",
			errorRate: "错误率",
			errorsReliability: "错误与可靠性",
			failedOfRequests: "{failed} 次失败 / 共 {total} 次请求",
			highestErrorDays: "错误率最高的日期",
			inferenceReference: "推理（参考）",
			latestUsage: "此项目的最新用量",
			loadingRecentActivity: "正在加载最近活动...",
			loadingReliability: "正在加载可靠性数据...",
			noActivity: "暂无活动数据",
			overallErrorCache: "此周期的整体错误率和缓存率",
			recentActivity: "最近活动",
			requestsTokens: "{requests} 次请求 - {tokens} token",
			saved: "-${amount} 已节省",
		},
		devPassCard: {
			cancelAnytime: "/月 - 可随时取消",
			collapse: "收起 DevPass 卡片",
			expand: "展开 DevPass 卡片",
			open: "打开 DevPass",
			separateProduct: "独立产品",
			valueDescription:
				"面向 Claude Code、OpenCode、Cursor 和各类编码工具的固定月费套餐。",
			valueHighlight: "$1 可转换为 $3",
			valueSuffix: "按供应商价格计算的模型用量。",
		},
		orgDiscounts: {
			activeDescription: "应用到你组织 API 用量的有效折扣",
			all: "全部",
			discount: "折扣",
			discountOff: "{percent} 优惠",
			expires: "过期时间",
			expiresLabel: "过期时间：",
			global: "全局",
			model: "模型",
			modelLabel: "模型：",
			never: "永不过期",
			noActive: "暂无有效折扣",
			noActiveDescription: "当组织获得折扣后，会显示在这里。",
			noOrganization: "未选择组织",
			org: "组织",
			provider: "供应商",
			providerLabel: "供应商：",
			reason: "原因",
			scope: "范围",
			title: "你的折扣",
		},
		orgPreferences: {
			billingEmail: "账单邮箱",
			billingEmailDescription: "管理组织的账单邮箱地址。",
			organizationId: "组织 ID",
			organizationIdDescription: "在 API 或支持沟通中引用组织时使用此 ID。",
			organizationName: "组织名称",
			organizationNameDescription: "管理组织名称。",
			title: "偏好设置",
		},
		orgReferrals: {
			avgMonthlySpend: "每位用户月均花费",
			bonusQuestion: "想给被推荐用户提供充值奖励？",
			bonusText:
				"请联系 {email} 为你的邀请页开启奖励，被推荐用户首次充值最高可获得 50% 额外额度。",
			copyDirectLink: "复制直接推荐链接",
			copyInvitationLink: "复制邀请页链接",
			directLink: "直接链接",
			directLinkDescription: "将用户引导到首页",
			earnCredits: "赚取额度",
			earnCreditsDescription: "获得其 LLM 花费的 1% 作为额度",
			earningsNote: "收益会在折扣抵扣后计算",
			eligibleDescription: "你需要至少充值 $100 额度，才能获得推荐计划资格",
			example: "示例",
			estimateDisclaimer: "这是简化估算。实际额度取决于真实用量和折扣。",
			estimateFormula:
				"{users} 位用户 x ${spend} 月花费 x 1% = 每月 ${credits} 额度",
			estimatedMonthlyCredits: "预计每月额度",
			howItWorks: "工作方式",
			howItWorksDescription: "推荐新用户即可赚取额度",
			invitationDescription: "带有你组织名称的个性化落地页",
			invitationPage: "邀请页",
			lifetimeStats: "推荐计划的累计统计",
			noOrganization: "未选择组织",
			notEligible: "暂未符合资格",
			notes: "重要说明",
			payoutNote: "推荐额度可用于 LLM 调用，但不能提现",
			referralLinkDescription:
				"选择推荐分享方式。两个链接都会以相同方式追踪推荐。",
			referralPerformance: "推荐表现与收益",
			shareLink: "你的推荐链接",
			shareLinkDescription: "将推荐链接发送给朋友和同事",
			signUp: "对方注册",
			signUpDescription: "当对方使用你的链接创建账号",
			title: "推荐奖励",
			totalEarnings: "总收益",
			totalTopUps: "你的累计充值",
			unlockAmount: "还差 ${amount} 可解锁推荐",
			unlimitedReferrals: "推荐用户数量没有上限",
			usersReferred: "已推荐用户",
			creditsAddedAutomatically: "额度会自动添加到账户",
			onceEligible:
				"符合资格后，你将从被推荐用户的所有 LLM 花费中获得 1% 额度奖励。额度会直接加入你的账户余额。",
			yourStats: "你的统计",
		},
		referralBanner: {
			email: "邮箱",
			howFound: "你是如何发现我们的？",
			other: "其他",
			recommendation: "朋友推荐",
			search: "搜索",
			skip: "跳过",
			sourceDescription: "这有助于我们了解用户如何发现 doteb。",
		},
		referralDialog: {
			avgMonthlySpend: "每位用户月均花费",
			creditsPercent: "被推荐用户 LLM 花费的 1%",
			earnCreditsDescription: "从你推荐的每位用户获得 {percent}。",
			earnCreditsPercent: "所有 LLM 花费的 1%",
			estimateDisclaimer: "这是简化估算。实际额度取决于真实用量和折扣。",
			estimateFormula:
				"{users} 位用户 x ${spend} 月花费 x 1% = 每月 ${credits} 额度",
			estimatedMonthlyCredits: "预计每月额度",
			example: "示例",
			howItWorks: "工作方式",
			inviteEarn: "邀请并赚取额度",
			manageReferralSettings: "管理推荐设置",
			monthlyGoal: "目标：${amount}",
			referredUsers: "推荐用户数",
			runNumbers: "估算收益",
			seeCreditsStack: "查看额度增长",
			seeCreditsStackDescription: "估算使用推荐链接后每月可能获得的额度。",
			shareLink: "分享你的链接",
			stepCopy:
				"从控制台复制你的专属推荐链接，并分享给可能受益于 doteb 的用户和团队。",
			stepEarn: "自动获得其{percent}花费作为额度。可在控制台实时查看收益。",
			stepEarnPercent: "LLM 花费的 1%",
			stepTopUp:
				"在组织中至少充值 {amount} 额度，即可获得资格并访问专属推荐链接。",
			thisMonth: "本月",
			trackEarnings: "追踪你的月度收益",
		},
		transactions: {
			amount: "金额",
			date: "日期",
			description: "描述",
			errorDescription: "加载交易记录出错",
			failedLoad: "交易记录加载失败。请稍后重试。",
			history: "交易记录",
			loadingDescription: "正在加载组织交易记录...",
			noOrganization: "请选择组织以查看交易记录。",
			noTransactions: "没有交易记录",
			organization: "组织：{organization}",
			status: "状态",
			statuses: {
				completed: "已完成",
				failed: "失败",
				pending: "待处理",
			},
			title: "交易记录",
			types: {
				credit_refund: "额度退款",
				credit_topup: "额度充值",
				subscription_cancellation: "订阅取消",
				subscription_renewal: "订阅续费",
				subscription_start: "订阅开始",
			},
			type: "类型",
			viewHistory: "查看组织交易记录和账单详情",
		},
		unauthorized: {
			goToDashboard: "返回控制台",
			message: "你无权访问此{resource}，或它不存在。",
			organization: "组织",
			project: "项目",
			title: "未授权",
		},
	},
} satisfies UiMessageModule;
